/**
 * Helpers for displaying (possibly fractional) service/package quantities.
 *
 * Service Packages v2 allows fractional quantities (2 decimals, min 0.01)
 * on package items, redemption balances, and per-syringe / per-unit booked
 * services. These formatters keep the display clean: at most 2 decimals,
 * trailing zeros stripped ("2.50" -> "2.5", "3.00" -> "3").
 */

import { ServicePriceBasis } from "../types/context";
import { formatCurrency } from "./cashDrawerUtils";

/** Format a quantity with up to 2 decimals, stripping trailing zeros. */
export function formatQty(value: number): string {
  if (!Number.isFinite(value)) return "0";
  const rounded = Math.round(value * 100) / 100;
  return String(rounded);
}

/**
 * Unit noun for a price basis ("syringe(s)" / "unit(s)"), pluralized for
 * the given quantity. Returns "" for per_service (no unit suffix shown).
 */
export function unitLabelForBasis(
  basis: ServicePriceBasis | string | null | undefined,
  quantity = 2,
): string {
  if (basis === "per_syringe") return quantity === 1 ? "syringe" : "syringes";
  if (basis === "per_unit") return quantity === 1 ? "unit" : "units";
  return "";
}

/** True when the basis carries fractional quantities (syringes/units). */
export function isPerUnitBasis(
  basis: ServicePriceBasis | string | undefined | null,
): basis is "per_syringe" | "per_unit" {
  return basis === "per_syringe" || basis === "per_unit";
}

/**
 * The "{qty} {unit} × {price}" breakdown for a per-syringe / per-unit line,
 * e.g. "2.5 units × $12.00" or "3 syringes × $12.00". Returns null for a
 * per_service (flat) basis, which has no unit breakdown. Shared by the
 * transaction detail row and the appointment summary so both read identically.
 */
export function perUnitBreakdown(
  quantity: number | undefined,
  basis: ServicePriceBasis | string | undefined | null,
  unitPrice: number,
): string | null {
  if (!isPerUnitBasis(basis)) return null;
  const qty = quantity ?? 1;
  return `${formatQty(qty)} ${unitLabelForBasis(basis, qty)} × ${formatCurrency(
    unitPrice,
  )}`;
}
